// ===========================================================================
// ReHome — Google Gemini AI Service (client-side wrappers)
// 3 core functions (plan.md) + 5 design_plan extras for prototype
//
// Every actual Gemini call happens server-side in the `gemini-proxy` Supabase
// Edge Function (supabase/edge-functions/gemini-proxy) — the API key must
// never ship inside a browser bundle. These wrappers keep the exact same
// signatures and Hungarian fallback templates the callers already rely on;
// only the transport changed (direct SDK call -> functions.invoke proxy).
// ===========================================================================

import { supabase, isSupabaseConfigured } from './supabase';
import type { Animal, LifestyleProfile, MatchResult } from './types';

/**
 * "AI features are reachable" — true whenever the app can reach the
 * gemini-proxy Edge Function at all. It does NOT guarantee the function
 * actually has a GEMINI_API_KEY configured server-side; that graceful
 * degradation happens per-call (a missing/failed result just falls back to
 * the template below), matching how every caller already treats this as an
 * optional "garnish", never a hard dependency.
 */
export const isGeminiConfigured = (): boolean => isSupabaseConfigured();

/**
 * Invoke the gemini-proxy Edge Function. Returns null on ANY failure
 * (function unreachable, no server-side API key, Gemini error, bad JSON) —
 * callers always have their own fallback for a null result.
 */
const callGeminiProxy = async <T>(action: string, payload: unknown): Promise<T | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('gemini-proxy', { body: { action, payload } });
    if (error) throw error;
    return (data?.result ?? null) as T | null;
  } catch {
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CORE FUNCTIONS (plan.md — 3 required)
// ═══════════════════════════════════════════════════════════════════════════

export const generateAnimalBio = async (
  name: string, species: string, breed: string, traits: string[]
): Promise<string> => {
  const fallback = `${name} egy ${species.toLowerCase()}${breed ? ` (${breed})` : ''}, aki szerető otthonra vágyik. ${traits.length > 0 ? `Jellemzői: ${traits.join(', ')}.` : ''}`;
  if (!isGeminiConfigured()) return fallback;
  const result = await callGeminiProxy<string>('generateAnimalBio', { name, species, breed, traits });
  return result?.trim() || fallback;
};

export const generateMatchReason = async (
  animal: { name: string; species: string; breed: string | null; traits: string[] },
  query: string
): Promise<string | null> => {
  if (!isGeminiConfigured()) return null;
  const result = await callGeminiProxy<string>('generateMatchReason', { animal, query });
  return result?.trim() || null;
};

export const generateContractSummary = async (animalName: string, adopterName: string): Promise<string> => {
  const fallback = `Gratulálunk, ${adopterName}! ${animalName} mostantól hivatalosan is a családod tagja.`;
  if (!isGeminiConfigured()) return fallback;
  const result = await callGeminiProxy<string>('generateContractSummary', { animalName, adopterName });
  return result?.trim() || fallback;
};

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN PLAN EXTRAS (prototype components)
// ═══════════════════════════════════════════════════════════════════════════

export const findSmartMatches = async (userQuery: string, animals: Animal[]): Promise<MatchResult[]> => {
  if (!isGeminiConfigured()) return [];
  const result = await callGeminiProxy<MatchResult[]>('findSmartMatches', { userQuery, animals });
  return result || [];
};

export const calculateLifestyleMatch = async (profile: LifestyleProfile, animals: Animal[]): Promise<MatchResult[]> => {
  if (!isGeminiConfigured()) return [];
  const result = await callGeminiProxy<MatchResult[]>('calculateLifestyleMatch', { profile, animals });
  return result || [];
};

export interface IntakeDescriptionInput {
  name: string;
  species: string;
  breed: string;
  gender?: string | null;
  age?: number | null;
  color?: string | null;
  isVaccinated?: boolean | null;
  isNeutered?: boolean | null;
  isDewormed?: boolean | null;
  /** Staff-only bekerülési körülmények — csak promptba megy, sosem a kimenetbe szó szerint. */
  circumstances?: string | null;
  /** Szabad szöveges megfigyelések (pl. "félénk", "sovány", "seb az oldalán"). */
  observations?: string | null;
}

/**
 * Combines the already-collected structured intake fields with free-text
 * staff observations into an adopter-facing bio + a short trait list.
 * Unlike the old intake flow, this does NOT re-guess species/breed/age —
 * those are entered directly by staff (see SpeciesBreedSelect) — it only
 * turns everything already known into a ready-to-use, editable description.
 */
export const generateIntakeDescription = async (
  input: IntakeDescriptionInput
): Promise<{ bio: string; traits: string[] }> => {
  const fallback = { bio: `${input.name} egy ${input.species.toLowerCase()}${input.breed ? ` (${input.breed})` : ''}, aki szerető otthonra vágyik.`, traits: [] };
  if (!isGeminiConfigured()) return fallback;

  const result = await callGeminiProxy<{ bio: string; traits: string[] }>('generateIntakeDescription', { input });
  if (!result?.bio) return fallback;
  return { bio: result.bio, traits: Array.isArray(result.traits) ? result.traits : [] };
};

export interface TraitNormalization {
  /** A beküldött kifejezés, változatlanul. */
  input: string;
  /** A használandó kulcsszó — meglévő szótári címke, vagy új javaslat. */
  canonical: string;
  /** true, ha a szótárban nincs rokon értelmű megfelelője. */
  isNew: boolean;
}

/**
 * Eldönti a még ismeretlen tulajdonság-kifejezésekről, hogy melyik meglévő
 * szótári kulcsszót jelentik ("kedves" -> "Barátságos"), vagy hogy új
 * fogalmak-e. Csak a transzport — a cache-elést és a DB-írást a
 * traitVocabulary.ts végzi, ez a függvény csak a cache-miss szavakat kapja.
 * Üres tömb = az AI nem elérhető vagy nem tudott dönteni (a hívónak van
 * fallbackje: a szót változatlanul, szótárba írás nélkül tartja meg).
 */
export const normalizeTraitCandidates = async (
  candidates: string[],
  vocabulary: string[]
): Promise<TraitNormalization[]> => {
  if (candidates.length === 0 || !isGeminiConfigured()) return [];
  const result = await callGeminiProxy<TraitNormalization[]>('normalizeTraits', { candidates, vocabulary });
  return Array.isArray(result) ? result : [];
};

export const generateBehavioralAdvice = async (userQuestion: string, species: string): Promise<string> => {
  if (!isGeminiConfigured()) return 'Az AI tanácsadó jelenleg nem elérhető. Kérlek vedd fel a kapcsolatot a menhellyel.';
  const result = await callGeminiProxy<string>('generateBehavioralAdvice', { userQuestion, species });
  return result || 'Próbálj meg konzultálni egy állatorvossal vagy a menhellyel.';
};

export const analyzeHealthNotes = async (rawNotes: string): Promise<{ summary: string; urgency: 'Low' | 'Medium' | 'High'; tasks: string[] }> => {
  if (!isGeminiConfigured()) return { summary: 'Nincs API kulcs.', urgency: 'Low', tasks: [] };
  const result = await callGeminiProxy<{ summary: string; urgency: 'Low' | 'Medium' | 'High'; tasks: string[] }>('analyzeHealthNotes', { rawNotes });
  return result || { summary: 'Hiba.', urgency: 'Low', tasks: [] };
};
